<template>
  <article v-if="article" class="flex flex-col">
    <div class="mb-[120px] flex gap-[20px]">
      <div class="flex w-full max-w-[100%] flex-col xl:w-3/4 2xl:w-4/5">
        <h1
          v-if="isOldArticleFormat"
          class="mb-[28px] text-[32px] font-medium leading-[38px]"
          v-text="article.title"
        />
        <CommonBlocksImageArticleBlock
          v-if="isOldArticleFormat && !isMainImageHidden && article.image"
          class="mb-[32px]"
          is-social
          :data="article"
          :published-at="article.published_at"
          :src="article.image.src"
        />

        <CommonBlocksCertificatesBlock
          v-if="isCertificates"
          :text="article?.body"
        />

        <div v-else-if="isBooks" class="page-body mb-[65px]">
          <CommonBlocksBookshelfBlock
            v-for="(bookshelf, index) in bookshelfs"
            :key="index"
            class="mb-5 last:mb-0"
            :bookshelf="bookshelf"
            :expanded="bookshelfs.length === 1"
          />
        </div>

        <div v-else-if="article?.json_body">
          <div
            class="flex justify-between text-[14px] leading-[18px] text-new-black-light"
          >
            <div
              class="flex cursor-pointer items-center px-4 py-2"
              @click="handleBookmark()"
            >
              <SvgIcon
                name="bookmark"
                class="mr-2 size-4"
                :class="{ 'text-ruby-red-100': isBookmarked }"
              ></SvgIcon>
              <div>В закладки</div>
            </div>
          </div>
          <EditorParser :json-str="article?.json_body">
            <EditorMainImage
              :image-src="getSrc"
              :title="article.title"
              :announce="article.announce"
              :source="article.cover_source"
              :description="article.cover_description"
              :time="article.time"
            />
          </EditorParser>
        </div>

        <div
          v-else
          ref="pageBodyRef"
          class="page-body mb-[65px]"
          v-html="article.body"
        />
        <!-- <CommonMobileBannerBlock /> -->
        <CommonBlocksNewTagsBlock
          v-if="tags"
          class="mb-[80px]"
          v-bind="{ tags }"
        />

        <CommonSocialsSocialRedesign
          v-if="article.id"
          :id="article.id"
          :model-type="article.model_type"
          :views="article.views"
          class="mb-[80px]"
        />

        <ClientOnly>
          <div
            v-if="article.authors?.length"
            class="mx-auto flex w-full flex-col max-xl:w-2/3 max-sm:w-full"
          >
            <h3 class="mb-[36px] text-[24px] font-medium">Автор</h3>
            <div class="grid grid-flow-row gap-5">
              <CommonAuthorInfo
                v-for="author in article.authors"
                :key="author.id"
                :author="author"
              />
            </div>
          </div>
        </ClientOnly>
      </div>
      <CommonBlocksRightArticleBlock class="xl:w-1/4 2xl:w-1/5" />
    </div>
    <!-- <CommonCommentsBlock
      :comments="articleSlug?.data?.value?.comments"
      :model-type="articleSlug?.data?.value?.model_type"
      :model-id="articleSlug?.data?.value?.id"
    /> -->
    <CommonBlocksPageFooterBlocks />
  </article>
</template>

<script setup>
import api from '@/services/api/api';

definePageMeta({
  layout: 'marginless',
});

const BOOK_IDS = [
  6760, 6761, 6792, 6807, 6808, 6817, 6818, 6819, 6820, 6821, 6822, 6823, 6824,
  6825, 6826, 6827, 6828, 6829, 6830, 6831, 6832, 6833, 6834, 6835, 6837, 6838,
  6839, 6840, 6841, 6842, 6843, 6844, 6872, 6873, 6874, 6875, 6876, 6877, 6878,
  6879, 6943, 6944, 6945, 6946, 6947, 6948, 6949, 6950, 14857, 14858, 14859,
  14860, 14861, 14862, 14863, 14864, 14865, 14866, 14867, 14868, 14869, 14870,
  14871, 14872, 14873,
];
const CERTIFICATE_IDS = [6766];

const route = useRoute();
const toast = useToast();
const slug = route?.params?.slug;
const robots = ref('all');
const pageBodyRef = ref(null);
const { articleSlug } = useArticleBySlug(slug);
const { isDesktop, isPad, isMobile } = useBreakpoint();

/** computed */
const article = computed(() => articleSlug?.data?.value);

const tags = computed(() => article.value?.tags);

const seo = computed(() => article.value?.seo);

const isBooks = computed(() => BOOK_IDS.includes(article.value?.id));

const isCertificates = computed(() =>
  CERTIFICATE_IDS.includes(article.value?.id),
);

const isOldArticleFormat = computed(
  () =>
    isBooks.value || isCertificates.value || article.value?.json_body === null,
);

const bookshelfs = computed(() => {
  if (!isBooks.value) return null;
  const books = article.value?.body.split('<p></p>');
  const bookshelfs = [];
  let currentBookshelf;
  books.forEach((item) => {
    if (item.toLowerCase().includes('полка №')) {
      const title = item.replace('<p>', '').replace('</p>', '');
      currentBookshelf = {
        title,
        number: title.toLowerCase().match('полка №(.*)')[1],
        books: [],
      };
      bookshelfs.push(currentBookshelf);
      return;
    }
    const book = item.replaceAll('<p>', '').split('</p>');
    if (!book[0]) return;
    currentBookshelf.books.push({ title: book[0], author: book[1] });
  });
  const bookshelfNumber = route.query.bookshelf;
  if (!bookshelfNumber) return bookshelfs;
  const filteredBookshelf = bookshelfs.filter(({ number }) => {
    return number === bookshelfNumber;
  });
  return filteredBookshelf.length ? filteredBookshelf : bookshelfs;
});

const isMainImageHidden = computed(() => isCertificates.value);

const getSrc = computed(() => {
  const src =
    article.value?.cover_image ||
    (isDesktop.value && article.value?.image?.desktop) ||
    (isPad.value && article.value?.image?.desktop) ||
    (isMobile.value && article.value?.image?.mobile) ||
    article.value?.image?.src;
  return src;
});

const isBookmarked = computed(() => article.value?.has_bookmark);
/** // computed */

const handleBookmark = async () => {
  try {
    const { data: bookmark } = await api.socials.handleBookmark({
      model_type: article.value?.model_type,
      id: article.value?.id,
    });
    if (
      bookmark.err &&
      bookmark.err === 'You are not authorized or there are no such user'
    ) {
      toast.error('Необходимо авторизоваться!');
      return;
    }
    if (bookmark === 'Ok') {
      toast.success('Добавлено в избранное!');
    }
    if (bookmark === 'Deleted') {
      toast.success('Удалено из избранного!');
    }
    articleSlug.refetch();
  } catch (e) {
    toast.error(`${e}`);
  }
};

onMounted(() => {
  renderImageFooter(pageBodyRef.value);
});

if (seo.value) {
  const arr = [];
  if (seo.value.index === false) arr.push('noindex');
  if (seo.value.follow === false) arr.push('nofollow');
  if (seo.value.archive === false) arr.push('noarchive');
  if (arr.length > 0) {
    robots.value = arr.reduce((previousValue, currentValue) => {
      return `${previousValue}, ${currentValue}`;
    });
  }
}
useHead(
  computed(() => ({
    title: `${
      article.value?.title ? article.value?.title + ' |' : ''
    } Читать статьи по истории РФ для школьников и студентов`,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: `Статьи о российской истории. ${
          seo.value?.metadescription ?? article.value?.title ?? ''
        }. Информация о Федеральном портале Истории России. Российское военно-историческое общество. Мы знаем правду.`,
      },
      {
        hid: 'keywords',
        name: 'keywords',
        content: article.value?.title ?? '',
      },
      {
        'http-equiv': 'Last-Modified',
        content: new Date(seo.value?.last_modified).toUTCString(),
      },
      {
        hid: 'robots',
        name: 'robots',
        content: robots.value ?? '',
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: article.value?.canonical_url || `https://histrf.ru${route.path}`,
      },
    ],
  })),
);
</script>
